/**
 * Bid role administration (P4) — `/bid-role-assignments`.
 *
 * The grantor is NEVER sent from here: the backend resolves it from the caller's token, so an audit
 * row for a privilege change cannot be attributed to someone the caller merely typed.
 *
 * Reuses `referenceDataService`'s `toError`, which unwraps both the standard envelope and
 * `validate_json`'s raw `{errors: {...}}` body — a 400 from the role CHECK should read as
 * "role: must be one of…", not "Request failed with status code 400". (It is imported, not copied:
 * two divergent normalisers would report the same backend error two different ways.)
 */
import HttpService from '../../AppSettings/services/HttpService';
import { toError } from './referenceDataService';

const http = new HttpService();

/** The five roles the DB CHECK allows (sql/migrations/0004_bid_roles.sql). */
export const BID_ROLES = [
  'estimator',
  'pm',
  'executive',
  'superintendent',
  'bid_administrator',
] as const;

export type BidRole = (typeof BID_ROLES)[number];

/** What each role can do — mirrors the capability map in config/roles/roles.py, for display only. */
export const ROLE_SUMMARY: Record<BidRole, string> = {
  estimator: 'Sees dollars · can create and edit bids',
  pm: 'Sees dollars · can create and edit bids',
  executive: 'Sees dollars · read-only',
  superintendent: 'Schedule and quantities only — never a dollar value',
  bid_administrator: 'Everything, plus reference-data admin and role assignment',
};

export interface BidRoleAssignment {
  id: string;
  employeeid: string;
  role: BidRole;
  granted_by: string | null;
  granted_at: string | null;
}

export async function listBidRoleAssignments(): Promise<BidRoleAssignment[]> {
  try {
    const res = await http.get<any>('/bid-role-assignments');
    return res?.data?.assignments ?? [];
  } catch (err) {
    throw toError(err);
  }
}

export async function grantBidRole(
  employeeid: string,
  role: BidRole,
): Promise<BidRoleAssignment | undefined> {
  try {
    const res = await http.post<any>('/bid-role-assignments', { employeeid, role });
    return res?.data?.assignment;
  } catch (err) {
    throw toError(err);
  }
}

export async function revokeBidRole(employeeid: string, role: BidRole): Promise<void> {
  try {
    await http.delete<any>(`/bid-role-assignments/${employeeid}/${role}`);
  } catch (err) {
    throw toError(err);
  }
}
